import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  /** User-entered name of the thing being confirmed (e.g. an action label); shown isolated. */
  subject?: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Destructive action: the confirm button uses the danger style. */
  danger?: boolean;
  /** Pin direction. Uninstall sets `ltr` so a translated page behind it cannot flip the dialog. */
  dir?: 'ltr' | 'rtl';
}

interface PendingConfirm {
  options: ConfirmDialogOptions;
  resolve: (confirmed: boolean) => void;
}

let enqueueConfirm: ((request: PendingConfirm) => void) | null = null;

/**
 * In-app replacement for window.confirm(). Resolves true on confirm, false on
 * cancel / Escape / backdrop click. Requires <ConfirmDialogHost /> to be
 * mounted (App does it); without a host it resolves false so a destructive
 * action never runs unconfirmed.
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  if (!enqueueConfirm) {
    console.error('confirmDialog called without a mounted ConfirmDialogHost');
    return Promise.resolve(false);
  }
  const enqueue = enqueueConfirm;
  return new Promise((resolve) => enqueue({ options, resolve }));
}

export function ConfirmDialogHost() {
  const [queue, setQueue] = useState<PendingConfirm[]>([]);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const current = queue[0];

  useEffect(() => {
    enqueueConfirm = (request) => setQueue((prev) => [...prev, request]);
    return () => {
      enqueueConfirm = null;
    };
  }, []);

  useEffect(() => {
    if (!current) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Focus Cancel first: Enter on a destructive dialog should not confirm by accident.
    cancelRef.current?.focus();
  }, [current]);

  if (!current) return null;

  const close = (confirmed: boolean) => {
    current.resolve(confirmed);
    setQueue((prev) => prev.slice(1));
    returnFocusRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(false);
    } else if (e.key === 'Tab') {
      // Two-button focus trap.
      e.preventDefault();
      const next = document.activeElement === cancelRef.current ? confirmRef.current : cancelRef.current;
      next?.focus();
    }
  };

  return (
    <div
      className="confirm-dialog__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close(false);
      }}
    >
      <div
        className="confirm-dialog"
        dir={current.options.dir}
        lang={current.options.dir === 'ltr' ? 'en' : undefined}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        onKeyDown={handleKeyDown}
      >
        <h3 id="confirm-dialog-title" className="confirm-dialog__title">{current.options.title}</h3>
        {current.options.subject && (
          <p className="confirm-dialog__subject">
            <bdi>{current.options.subject}</bdi>
          </p>
        )}
        <p id="confirm-dialog-message" className="confirm-dialog__message">{current.options.message}</p>
        <div className="confirm-dialog__actions">
          <button ref={cancelRef} type="button" className="btn btn--secondary" onClick={() => close(false)}>
            {current.options.cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`btn ${current.options.danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={() => close(true)}
          >
            {current.options.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
